import { Component, Input, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Subject } from 'rxjs';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import {
  Flag,
  Notification,
  NotificationStatus,
  NotificationType,
  QuestionnaireType
} from '../../../../API';
import { NotificationContent, NotificationPriority } from '../inbox.model';
import { InboxService } from '../inbox.service';

@Component({
  selector: 'csi-notification-card',
  templateUrl: './notification-card.component.html',
  styleUrls: ['./notification-card.component.scss']
})
export class NotificationCardComponent implements OnInit {
  @Input() notification: Notification;
  @Input() notificationType: NotificationType;
  @Input() notificationSelected$: Subject<string>;

  public notificationStatus: NotificationStatus;

  public selected = false;
  public priority: NotificationPriority = 'low';
  public doublePriority: boolean;
  public Flag = Flag;
  public questionnaireTypeLabel = '';
  constructor(
    private inboxService: InboxService,
    public questionnaireRequestService: QuestionnaireRequestService,
    private translocoService: TranslocoService
  ) {}

  public notificationContent: NotificationContent;

  ngOnInit() {
    if (this.notification.content) {
      this.notificationContent = JSON.parse(this.notification.content);
    }

    this.questionnaireTypeLabel = this.computeQuestionnaireTypeLabel();

    this.notificationStatus = this.inboxService.getNotificationStatusCorrespondingToDoctorId(
      this.notification.properties,
      this.inboxService.loggedInDoctor
    );

    this.notificationSelected$.subscribe(id => {
      if (this.notification.id === id) {
        this.selected = true;
        if (!this.notificationStatus.read) {
          this.inboxService.setNotificationReadTrue(this.notification);
          // this.updateNotificationReadStatus();
        }
      } else {
        if (this.selected && !this.notificationStatus.read) {
          this.notificationStatus.read = true;
          this.inboxService.decrementUnreadNotificationCount(this.notificationType);
        }
        this.selected = false;
      }
    });
    this.setPriority();
    this.setDoublePriority();
  }

  ngOnDestroy() {
    // if (this.notificationSelected$.closed === false || this.notificationSelected$.isStopped) {
    //   this.notificationSelected$.unsubscribe();
    // }
  }

  parseQuestionnaireText(text: string): string {
    if (!text.startsWith('Patient ') || !text.includes(' just completed a dry eye questionnaire')) {
      return text;
    }

    const nameEndIndex = text.indexOf(' just completed');
    const patientName = text.substring(8, nameEndIndex); // "Patient " is 8 chars

    const hasResults = text.includes(' with the following results:');
    const typeLabel = this.questionnaireTypeLabel;

    const translatedPrefix = this.translocoService.translate('Patient');
    let translatedAction: string;
    if (typeLabel) {
      translatedAction = hasResults
        ? this.translocoService.translate('completedTypedQuestionnaireWithResults', {
            questionnaireType: typeLabel
          })
        : this.translocoService.translate('completedTypedQuestionnaire', {
            questionnaireType: typeLabel
          });
    } else {
      translatedAction = hasResults
        ? this.translocoService.translate('completedDryEyeQuestionnaireWithResults')
        : this.translocoService.translate('completedDryEyeQuestionnaire');
    }

    const resultsText = hasResults
      ? text.substring(text.indexOf(' with the following results:') + 29)
      : '';

    return `${translatedPrefix} ${patientName} ${translatedAction}${resultsText}`;
  }

  private computeQuestionnaireTypeLabel(): string {
    if (!this.notificationContent) {
      return '';
    }

    const friendlyNameMap = this.questionnaireRequestService
      .questionnaireTypeToFriendlyNameMap;

    if (this.notificationContent.questionnaireType) {
      const rawType = this.notificationContent.questionnaireType as QuestionnaireType;
      return friendlyNameMap[rawType] || this.notificationContent.questionnaireType;
    }

    if (!this.notificationContent.questionnaireScores) {
      return '';
    }

    let scores: { [key: string]: any };
    try {
      scores = JSON.parse(this.notificationContent.questionnaireScores);
    } catch (e) {
      return '';
    }

    const scoreKeys = scores ? Object.keys(scores).map(k => k.toUpperCase()) : [];
    if (scoreKeys.length === 0) {
      return '';
    }

    const hasOsdi = scoreKeys.indexOf(QuestionnaireType.OSDI) !== -1;
    const hasSpeed = scoreKeys.indexOf(QuestionnaireType.SPEED) !== -1;
    const hasSpeedii = scoreKeys.indexOf(QuestionnaireType.SPEEDII) !== -1;

    if (hasSpeedii) {
      return friendlyNameMap[QuestionnaireType.SPEEDII] || 'SPEED II';
    }
    if (hasOsdi && hasSpeed && scoreKeys.length === 2) {
      return friendlyNameMap[QuestionnaireType.OSDIAndSPEED] || 'OSDI 12 and SPEED';
    }

    return scoreKeys
      .map(k => friendlyNameMap[k as QuestionnaireType] || k)
      .join(' & ');
  }

  parseIntakeText(text: string): string {
    if (!text.endsWith(' has submitted their patient intake form.')) {
      return text;
    }

    const patientName = text.replace(' has submitted their patient intake form.', '');
    return this.translocoService.translate('intakeFormSubmittedBody', { patientName });
  }

  parseIntakeSubject(subject: string): string {
    if (!subject.startsWith('New Patient Intake Form submitted by ')) {
      return subject;
    }

    const patientName = subject.replace('New Patient Intake Form submitted by ', '');
    return this.translocoService.translate('intakeFormSubmittedBy', { patientName });
  }

  parseConsentSubject(subject: string): string {
    if (!subject) {
      const consentFormName =
        (this.notificationContent && this.notificationContent.consentFormName) ||
        this.questionnaireTypeLabel ||
        '';
      if (consentFormName) {
        return this.translocoService.translate('patientFilledConsentWithType', {
          consentFormName
        });
      }
      return this.translocoService.translate('patientFilledConsent');
    }

    return subject;
  }

  parseConsentText(text: string): string {
    if (!text) {
      return this.parseConsentSubject('');
    }
    return text;
  }

  setPriority() {
    switch (this.notificationType) {
      case NotificationType.referral:
        const referralPriority = this.notificationContent.referralPriority;
        if (
          referralPriority &&
          (referralPriority === 'URGENT' || referralPriority === 'SEMI_URGENT')
        ) {
          this.priority = 'high';
        }
        break;
      case NotificationType.questionnaire:
        if (this.notificationContent.questionnaireScores) {
          const isAtRisk = this.questionnaireRequestService.isAtRisk(
            this.notificationContent.questionnaireScores
          );
          if (isAtRisk) {
            this.priority = 'high';
          }
        }
        break;
    }
  }

  setDoublePriority() {
    this.doublePriority = this.questionnaireRequestService.customSpeedThreshold(
      JSON.parse((this.notificationContent && this.notificationContent.questionnaireScores) || '{}')
        .SPEED
    );
  }

  onClick() {
    this.notificationSelected$.next(this.notification.id);
  }

  deleteNotification(event: Event) {
    event.stopPropagation();
    this.inboxService.deleteNotification(this.notification, this.notificationStatus.read);
  }

  flagNotification(event: Event) {
    event.stopPropagation();

    switch (this.notificationStatus.flag) {
      case Flag.none:
        this.notificationStatus.flag = Flag.important;
        break;
      case Flag.important:
        this.notificationStatus.flag = Flag.complete;
        break;
      case Flag.complete:
        this.notificationStatus.flag = Flag.important;
    }

    const notificationPropertiesInput = this.notification.properties.map(property => {
      return {
        doctorId: property.doctorId,
        status: {
          read: property.status.read,
          flag:
            this.inboxService.loggedInDoctor === property.doctorId
              ? this.notificationStatus.flag
              : property.status.flag
        }
      };
    });

    this.inboxService
      .updateNotification({
        id: this.notification.id,
        properties: notificationPropertiesInput
      })
      .subscribe(() => {});
  }
}
